<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { materialAssetsApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'
import { matchesUploadReceipt } from '@/utils/materialUploadReceipt.js'
import PastedTextField from '@/components/PastedTextField.vue'
import { createCapacityLoader, uploadCapacityIssue } from '@/utils/materialUploadState.js'

const router = useRouter()
const tenantId = computed(() => authStore.state.selectedTenant?.id || '')
const project = computed(() => authStore.state.assignment?.project || null)
const assets = ref([])
const uploadQueue = ref([])
const capacity = ref(null)
const capacityStatus = ref('idle'), capacityError = ref('')
const textFields = {}, textStates = ref({}), textSources = ref({})
const queueValid = computed(() => uploadQueue.value.length > 0 && uploadQueue.value.every((entry) => textStates.value[entry.id]?.valid && textStates.value[entry.id]?.value === entry.description))
const capacityIssue = computed(() => uploadCapacityIssue(uploadQueue.value.length, capacityStatus.value, capacity.value))
const queueFits = computed(() => capacityStatus.value === 'ready' && !capacityIssue.value)
const descriptionDrafts = ref({})
const savingDescriptionId = ref('')
const authorizationScope = ref('已获得企业授权，可用于本项目营销内容制作与任务下发')
const allowedPlatforms = ref(['xiaohongshu', 'douyin', 'tiktok'])
const canRemix = ref(true)
const privacyMasked = ref(false)
const authorizationConfirmed = ref(false)
const loading = ref(true)
const uploading = ref(false)
const errorMessage = ref('')
const stateMessage = ref('正在读取云端素材库…')
const thumbnailSources = ref({})
const activeAssets = computed(() => assets.value.filter((asset) => asset.status !== 'archived'))
const uploadedImageCount = computed(() => activeAssets.value.filter((asset) => String(asset.mime_type || '').startsWith('image/')).length)
const readyImageCount = computed(() => activeAssets.value.filter((asset) => (
  asset.status === 'ready' && asset.mime_type === 'image/jpeg'
)).length)
const pendingValidation = computed(() => activeAssets.value.some((asset) => (
  asset.status === 'needs_review' || asset.metadata?.validation_status === 'pending'
)))
const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))
let validationPollTimer = null
let validationPollCount = 0
let validationPollRunning = false
let disposed = false
let queueError = false, queueScope = '', libraryReadSequence = 0
const scopeKey = () => `${tenantId.value}/${project.value?.id}`
const capacityLoader = createCapacityLoader({ read: (...args) => materialAssetsApi.capacity(...args),
  isCurrent: scope => !disposed && `${scope.tenant}/${scope.project}` === scopeKey(),
  onState(status, value, error) { capacityStatus.value = status; capacity.value = value; capacityError.value = error },
})
const retryCapacity = () => capacityLoader.load({ tenant: tenantId.value, project: project.value?.id })

function revokeThumbnails() {
  Object.values(thumbnailSources.value).forEach((source) => URL.revokeObjectURL(source))
  thumbnailSources.value = {}
}

async function fetchThumbnail(asset) {
  if (!asset.thumbnail_url) return
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), 30000)
    try {
      const response = await fetch(asset.thumbnail_url, {
        credentials: 'include', cache: 'no-store', signal: controller.signal,
      })
      if (!response.ok) throw new Error(`thumbnail HTTP ${response.status}`)
      const blob = await response.blob()
      if (!blob.type.startsWith('image/') || blob.size === 0) throw new Error('thumbnail is empty')
      thumbnailSources.value = {
        ...thumbnailSources.value,
        [asset.id]: URL.createObjectURL(blob),
      }
      return
    } catch (error) {
      if (attempt === 2) throw error
      await wait(800)
    } finally {
      window.clearTimeout(timer)
    }
  }
}

async function hydrateThumbnails(currentAssets) {
  revokeThumbnails()
  const previewable = currentAssets.filter((asset) => asset.thumbnail_url)
  let loaded = 0
  for (const asset of previewable) {
    try {
      await fetchThumbnail(asset)
      loaded += 1
    } catch { /* keep the card and its audit metadata visible */ }
  }
  if (previewable.length && loaded < previewable.length) {
    stateMessage.value = `云端共有 ${activeAssets.value.length} 个有效素材；${loaded}/${previewable.length} 张预览已加载，其余会在刷新时自动重试。`
  }
}

async function fileSha256(file) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('')
}

async function prepareMaterialImageFile(file) {
  if (!(file instanceof File) || !String(file.type || '').startsWith('image/') || file.size <= 1_500_000) return file
  if (typeof createImageBitmap !== 'function') return file
  try {
    stateMessage.value = '正在压缩大图，减少网络中断…'
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, 2560 / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const context = canvas.getContext('2d', { alpha: false })
    context.fillStyle = '#FFFFFF'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close?.()
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88))
    if (!blob || blob.size >= file.size) return file
    const name = String(file.name || 'material.jpg').replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified })
  } catch {
    return file
  }
}

function onFile(event) {
  if (uploading.value) return
  const files = Array.from(event.target.files || [])
  if (files.length + uploadQueue.value.length > 14) {
    queueError = true
    errorMessage.value = '每次最多准备14张图片；先准备2张就可以开始。请减少本次选择。'
    event.target.value = ''
    return
  }
  if (!uploadQueue.value.length) queueScope = scopeKey()
  uploadQueue.value.push(...files.map((file) => ({ id: crypto.randomUUID(), file, description: '', error: '' })))
  event.target.value = ''
  if (queueError) { errorMessage.value = ''; queueError = false }
}

function removeQueued(entry) {
  uploadQueue.value = uploadQueue.value.filter(item => item.id !== entry.id)
  if (queueError || errorMessage.value === entry.error) { errorMessage.value = ''; queueError = false }
  if (!uploadQueue.value.length) queueScope = ''
  delete textStates.value[entry.id]; delete textSources.value[entry.id]; delete textFields[entry.id]
}

function hydrateDescriptionDrafts(currentAssets) {
  descriptionDrafts.value = Object.fromEntries(currentAssets.map((asset) => [
    asset.id, asset.enterprise_description || asset.metadata?.enterprise_description || '',
  ]))
}

function statusLabel(asset) {
  const validation = asset.metadata?.validation_status
  if (asset.status === 'ready' && validation === 'ready') return '图片可以使用'
  if (asset.status === 'needs_review' || validation === 'pending') return '正在自动优化图片'
  if (asset.status === 'blocked') return '这张图片无法识别，请换一张'
  if (asset.status === 'archived') return '已撤回'
  return asset.status
}

async function loadAssets() {
  const scope = scopeKey(), request = ++libraryReadSequence
  const current = () => !disposed && scope === scopeKey() && request === libraryReadSequence
  loading.value = true
  errorMessage.value = ''
  const list = materialAssetsApi.list(tenantId.value, project.value.id).then(async result => {
    if (!current()) return
    assets.value = result
    hydrateDescriptionDrafts(assets.value)
    stateMessage.value = `云端共有 ${activeAssets.value.length} 个有效素材；已验证图片 ${readyImageCount.value} 张，每天选择2张。`
    await hydrateThumbnails(assets.value)
    if (current()) scheduleValidationPoll()
  }).catch(error => {
    if (current()) errorMessage.value = error?.message || '素材列表读取失败，请重新读取素材库；选图和说明已保留。'
  })
  try { await Promise.all([list, retryCapacity()]) }
  finally { if (current()) loading.value = false }
}

function stopValidationPoll() {
  if (validationPollTimer) window.clearTimeout(validationPollTimer)
  validationPollTimer = null
  validationPollRunning = false
}

function scheduleValidationPoll() {
  stopValidationPoll()
  if (!pendingValidation.value || validationPollCount >= 45) return
  validationPollTimer = window.setTimeout(pollValidationStatus, 2000)
}

async function pollValidationStatus() {
  if (validationPollRunning) return
  validationPollRunning = true
  validationPollCount += 1
  try {
    const previousPending = pendingValidation.value
    const current = await materialAssetsApi.list(tenantId.value, project.value.id)
    assets.value = Array.isArray(current) ? current : []
    if (previousPending && !pendingValidation.value) {
      stateMessage.value = `云端校验已完成；已验证图片 ${readyImageCount.value} 张，每天选择2张。`
      await hydrateThumbnails(assets.value)
    } else if (pendingValidation.value) {
      stateMessage.value = `云端正在校验素材（${validationPollCount}/45），完成后本页会自动更新。`
    }
    errorMessage.value = ''
  } catch {
    stateMessage.value = '云端校验仍在进行，页面会继续自动读取，不需要手动刷新。'
  } finally {
    validationPollRunning = false
    scheduleValidationPoll()
  }
}

async function upload() {
  if (uploading.value) return
  uploading.value = true
  errorMessage.value = ''
  const scope = { tenant: tenantId.value, project: project.value.id }
  const context = `${scope.tenant}/${scope.project}`
  const batchMetadata = {
    role: 'material', authorizationScope: authorizationScope.value,
    allowedPlatforms: [...allowedPlatforms.value], canRemix: canRemix.value,
    privacyMasked: privacyMasked.value, proofLevel: 'strong',
  }
  try {
    if (!uploadQueue.value.length) { errorMessage.value = '请先选择图片。'; return }
    if (queueScope !== context) { errorMessage.value = '选图属于原项目，请返回原项目继续上传，文件和说明已保留。'; return }
    if (capacityStatus.value !== 'ready') await retryCapacity()
    if (!queueFits.value) return
    for (const entry of uploadQueue.value) {
      const valid = await textFields[entry.id]?.validate()
      if (disposed || context !== scopeKey()) return
      const state = textStates.value[entry.id]
      if (valid !== true || !state?.valid || state.value !== entry.description) {
        entry.error = textStates.value[entry.id]?.message || '请填写有效图片说明后重试。'
        errorMessage.value = `${entry.file.name}：${entry.error}`
        const field = document.querySelector(`[data-upload-id="${entry.id}"] textarea`)
        field?.scrollIntoView({ block: 'center' }); field?.focus(); return
      }
    }
    if (!authorizationConfirmed.value || !authorizationScope.value.trim()) { errorMessage.value = '请填写授权范围，并勾选素材授权确认。'; return }
    for (const entry of [...uploadQueue.value]) {
      if (disposed || context !== `${tenantId.value}/${project.value?.id}`) break
      entry.error = ''
      const succeeded = await uploadOne(entry, scope, batchMetadata)
      if (!succeeded) break
      uploadQueue.value = uploadQueue.value.filter((item) => item.id !== entry.id)
    }
    if (!uploadQueue.value.length) authorizationConfirmed.value = false
    if (!disposed && context === `${tenantId.value}/${project.value?.id}`) await loadAssets()
  } finally { uploading.value = false }
}

async function uploadOne(entry, scope, batchMetadata) {
  const description = entry.description
  try {
    if (await textFields[entry.id]?.validate() === false) throw new Error(textStates.value[entry.id]?.message || '请先修正图片说明。')
    if (description !== entry.description) return false
    const file = await prepareMaterialImageFile(entry.file)
    stateMessage.value = '正在上传并核对云端状态…'
    const digest = await fileSha256(file)
    const key = `material-${scope.project}-${digest}`
    const metadata = {
      ...batchMetadata,
      enterpriseDescription: description,
      pastedSource: textSources.value[entry.id] || null,
    }
    let uploaded = false
    let lastError = null
    for (let attempt = 1; attempt <= 3 && !uploaded; attempt += 1) {
      try {
        if (disposed) return false
        await materialAssetsApi.upload(scope.tenant, scope.project, file, metadata, { idempotencyKey: key })
        uploaded = true
      } catch (error) {
        lastError = error
        if (error?.code === 'MATERIAL_DUPLICATE' && error?.details?.asset) {
          uploaded = matchesUploadReceipt(error.details.asset, digest, description)
          if (uploaded) break
        }
        const recoverable = [
          'CLOUD_CONNECTION_ERROR', 'CLOUD_REQUEST_TIMEOUT', 'MATERIAL_STORAGE_UNAVAILABLE', 'MATERIAL_DUPLICATE',
        ].includes(error?.code)
        if (!recoverable) throw error
        stateMessage.value = `上传连接中断，正在核对云端并自动恢复（${attempt}/3）…`
        let current = null
        try { current = await materialAssetsApi.list(scope.tenant, scope.project) }
        catch { /* A failed read is not proof that the upload failed. */ }
        if (Array.isArray(current)) {
          assets.value = current
          // Keep conflicts outside the read-error catch: never swallow lost descriptions.
          uploaded = current.some((asset) => matchesUploadReceipt(asset, digest, description))
        }
        if (!uploaded && attempt < 3) await wait(attempt * 1500)
      }
    }
    if (!uploaded) throw lastError
    stateMessage.value = '上传完成，正在自动优化图片。'
    validationPollCount = 0
    return true
  } catch (error) {
    entry.error = error?.message || '上传尚未完成，文件和说明已保留，请核对后重试。'
    errorMessage.value = entry.error
    return false
  }
}

async function saveDescription(asset) {
  const description = String(descriptionDrafts.value[asset.id] || '')
  if (!await textFields[asset.id]?.validate() || description !== descriptionDrafts.value[asset.id]) return
  savingDescriptionId.value = asset.id
  errorMessage.value = ''
  try {
    await materialAssetsApi.updateDescription(tenantId.value, asset.id, description, textSources.value[asset.id] || null)
    await loadAssets()
    stateMessage.value = '图片说明已保存，只会用于以后下发的新任务。'
  } catch (error) {
    errorMessage.value = error?.message || '图片说明保存失败，请稍后重试。'
  } finally { savingDescriptionId.value = '' }
}

async function withdraw(asset) {
  if (!window.confirm(`确认撤回“${asset.name || asset.file_name}”？已下发任务中的版本仍保留审计记录。`)) return
  try {
    await materialAssetsApi.withdraw(tenantId.value, asset.id)
    await loadAssets()
  } catch (error) { errorMessage.value = error?.message || '撤回失败，请刷新后重试。' }
}

onMounted(async () => {
  if (!tenantId.value || !project.value?.id) {
    await router.replace(authStore.routeForCurrentState())
    return
  }
  await loadAssets()
})
onBeforeUnmount(() => {
  disposed = true
  capacityLoader.cancel()
  stopValidationPoll()
  revokeThumbnails()
})
</script>

<template>
  <section class="material-page" aria-labelledby="material-title">
    <header class="profile-header">
      <div>
        <p class="eyebrow">REAL MATERIAL LIBRARY</p>
        <h1 id="material-title">企业真实素材库</h1>
        <p>直接上传手机照片即可。系统会自动旋转、缩小并转换成 App 可以稳定读取的图片。</p>
      </div>
      <router-link class="text-button" to="/workspace/brain">返回项目大脑</router-link>
    </header>
    <p class="material-state" aria-live="polite">{{ stateMessage }}</p>
    <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
    <p class="material-guidance">先准备2张真实图片就可以开始，也可以按需要准备更多，每次最多14张。每张填写说明；上传只保存素材，不会自动下发。每天任务选择两张不同图片。</p>
    <p v-if="capacity">素材库 {{ uploadedImageCount }} 张，容量 {{ capacity.library_image_limit }} 张，剩余 {{ capacity.remaining_image_slots }} 张。</p>
    <div class="material-readiness" aria-label="任务素材准备状态">
      <strong>任务素材：</strong>
      <span :class="{ ready: readyImageCount >= 2 }">{{ readyImageCount >= 2 ? '素材库已有可用图片，请按任务日期选择两张并确认排期' : `图片准备进度 ${readyImageCount}/2` }}</span>
    </div>
    <p v-if="capacityStatus === 'error'" class="form-error" role="alert">{{ capacityError || '容量暂时读取失败，选图和说明已保留。' }}</p>
    <button v-if="capacityStatus === 'error'" type="button" class="secondary-action" @click="retryCapacity">重新读取容量</button>
    <form class="material-upload-card" novalidate @submit.prevent="upload">
      <label><span>选择真实图片（2张即可开始，最多14张）</span><input type="file" multiple :disabled="uploading" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" @change="onFile" /></label>
      <div v-for="entry in uploadQueue" :key="entry.id" :data-upload-id="entry.id" class="material-wide">
        <label><span>{{ entry.file.name }} — 图片说明（必填）</span>
          <PastedTextField v-model="entry.description" :ref="el => { textFields[entry.id] = el }" :tenant-id="tenantId" :project-id="project?.id" kind="material"
            :disabled="uploading" rows="3" required placeholder="图片里是什么、在哪里拍摄、能看到哪些细节？"
            @source="textSources[entry.id] = $event" @validation="textStates[entry.id] = $event" />
        </label>
        <p v-if="entry.error" class="form-error" role="alert">{{ entry.error }}</p>
        <button type="button" class="text-button" :disabled="uploading" @click="removeQueued(entry)">移除</button>
      </div>
      <label class="material-wide"><span>授权范围</span><textarea v-model="authorizationScope" rows="3" required /></label>
      <label class="check-row material-wide"><input v-model="authorizationConfirmed" type="checkbox" required /><span>我确认该文件是真实企业素材，并已取得用于本项目营销的授权。</span></label>
      <label class="check-row"><input v-model="canRemix" type="checkbox" /><span>允许裁切和排版</span></label>
      <label class="check-row"><input v-model="privacyMasked" type="checkbox" /><span>已完成隐私脱敏</span></label>
      <p v-if="capacityIssue" class="form-error" role="alert">{{ capacityIssue }}</p>
      <p v-if="!uploadQueue.length">请先选择图片，填写说明后上传。</p>
      <button class="secondary-action" type="submit" :disabled="uploading || !uploadQueue.length">{{ uploading ? '正在检查并上传，请勿关闭页面…' : `上传 ${uploadQueue.length} 张图片` }}</button>
    </form>
    <div v-if="loading && assets.length === 0" class="analysis-empty">正在同步云端权威状态…</div>
    <div v-else class="material-grid" :aria-busy="loading">
      <article v-for="asset in assets" :key="asset.id" class="material-card" :class="`material-${asset.status}`">
        <img v-if="thumbnailSources[asset.id]" :src="thumbnailSources[asset.id]" :alt="asset.name || asset.file_name" />
        <div v-else class="material-placeholder" aria-hidden="true">{{ asset.mime_type === 'application/pdf' ? 'PDF' : 'IMG' }}</div>
        <div>
          <h2>{{ asset.name || asset.file_name }}</h2><p>{{ statusLabel(asset) }}</p><small>已自动优化为手机可用图片</small>
          <label v-if="asset.status !== 'archived'" class="description-editor">
            <span>图片说明</span>
            <PastedTextField v-model="descriptionDrafts[asset.id]" :ref="el => { textFields[asset.id] = el }" :tenant-id="tenantId" :project-id="project?.id" kind="material" rows="3" required
              @source="textSources[asset.id] = $event" @validation="textStates[asset.id] = $event" />
            <small>修改后只影响以后下发的新任务。</small>
          </label>
          <button v-if="asset.status !== 'archived'" class="secondary-action" type="button" :disabled="savingDescriptionId === asset.id" @click="saveDescription(asset)">{{ savingDescriptionId === asset.id ? '正在保存…' : '保存图片说明' }}</button>
        </div>
        <button v-if="asset.status !== 'archived'" class="text-button" type="button" @click="withdraw(asset)">撤回</button>
      </article>
      <p v-if="assets.length === 0" class="analysis-empty">还没有素材。请上传企业已授权的真实图片或 PDF。</p>
    </div>
  </section>
</template>
